import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Recycle, Shield, FileCheck, Upload, Settings, 
  CheckCircle, AlertTriangle, Clock, ArrowUpDown,
  Plus, Search, Download, Send, Eye,
  FileText, Trash2, RefreshCw, Link2, ChevronRight,
  Building2, Truck, Factory, Calendar,
  Lock, Wifi, WifiOff, Info, ArrowRight, ExternalLink,
  Hash, X, Save, Users, Edit2, Loader2, Bell, BellRing,
  LayoutDashboard, TrendingUp, Package, Scale, BarChart3, Target
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../services/authService";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const StatoLabel = ({ stato }) => {
  const config = {
    trasmesso: { color: "text-green-400 bg-green-500/15 border-green-500/30", label: "Trasmesso" },
    in_attesa: { color: "text-yellow-400 bg-yellow-500/15 border-yellow-500/30", label: "In Attesa" },
    in_elaborazione: { color: "text-amber-400 bg-amber-500/15 border-amber-500/30", label: "In Elaborazione" },
    errore: { color: "text-red-400 bg-red-500/15 border-red-500/30", label: "Errore" },
    bozza: { color: "text-zinc-400 bg-zinc-500/15 border-zinc-500/30", label: "Bozza" },
    completato: { color: "text-green-400 bg-green-500/15 border-green-500/30", label: "Completato" },
    in_transito: { color: "text-blue-400 bg-blue-500/15 border-blue-500/30", label: "In Transito" },
    emesso: { color: "text-cyan-400 bg-cyan-500/15 border-cyan-500/30", label: "Emesso" },
  };
  const c = config[stato] || config.bozza;
  return <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${c.color}`}>{c.label}</span>;
};

// ─── API helpers ────────────────────────────────────────────────
const api = async (path, opts = {}) => {
  try {
    const fetchOpts = { ...opts };
    if (!fetchOpts.headers) fetchOpts.headers = {};
    if (!fetchOpts.headers["Content-Type"] && !opts.body?.constructor?.name?.includes("FormData")) {
      fetchOpts.headers["Content-Type"] = "application/json";
    }
    const res = await authFetch(`${API}/api/rentri${path}`, fetchOpts);
    if (!res.ok) {
      if (res.status === 401) { window.location.href = "/"; return null; }
      return null;
    }
    return res.json();
  } catch (e) {
    console.error("RENTRI API error:", e);
    return null;
  }
};

// ─── Soggetti Modal ────────────────────────────────────────────
function SoggettiModal({ open, onClose, onSelect, tipo }) {
  const [soggetti, setSoggetti] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ragione_sociale: "", codice_fiscale: "", partita_iva: "", indirizzo: "", citta: "" });

  useEffect(() => {
    if (open) api(`/soggetti?tipo=${tipo}`).then(d => d && setSoggetti(d));
  }, [open, tipo]);

  const handleAdd = async () => {
    const res = await api("/soggetti", { method: "POST", body: JSON.stringify({ ...form, tipo }) });
    if (res) { setSoggetti(prev => [...prev, res]); setShowAdd(false); setForm({ ragione_sociale: "", codice_fiscale: "", partita_iva: "", indirizzo: "", citta: "" }); toast.success("Soggetto creato"); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="soggetti-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg max-h-[80vh] overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-emerald-400" />Seleziona {tipo === "trasportatore" ? "Trasportatore" : "Destinatario"}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {soggetti.map(s => (
          <button key={s.id} onClick={() => { onSelect(s); onClose(); }} className="w-full text-left p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-emerald-500/30 transition-all mb-2" data-testid={`soggetto-${s.id}`}>
            <p className="text-white font-medium text-sm">{s.ragione_sociale}</p>
            <p className="text-zinc-500 text-xs">{s.codice_fiscale} - {s.citta}</p>
          </button>
        ))}
        {soggetti.length === 0 && !showAdd && <p className="text-zinc-500 text-sm text-center py-4">Nessun {tipo} registrato</p>}
        {showAdd ? (
          <div className="space-y-3 mt-4 p-4 rounded-lg border border-zinc-700 bg-zinc-800/50">
            <input value={form.ragione_sociale} onChange={e => setForm(p => ({ ...p, ragione_sociale: e.target.value }))} placeholder="Ragione Sociale *" className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 outline-none focus:border-emerald-500" data-testid="soggetto-nome-input" />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.codice_fiscale} onChange={e => setForm(p => ({ ...p, codice_fiscale: e.target.value }))} placeholder="Codice Fiscale" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 outline-none focus:border-emerald-500" />
              <input value={form.partita_iva} onChange={e => setForm(p => ({ ...p, partita_iva: e.target.value }))} placeholder="Partita IVA" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 outline-none focus:border-emerald-500" />
            </div>
            <input value={form.indirizzo} onChange={e => setForm(p => ({ ...p, indirizzo: e.target.value }))} placeholder="Indirizzo" className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 outline-none focus:border-emerald-500" />
            <input value={form.citta} onChange={e => setForm(p => ({ ...p, citta: e.target.value }))} placeholder="Citta" className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 outline-none focus:border-emerald-500" />
            <div className="flex gap-2">
              <Button onClick={handleAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" disabled={!form.ragione_sociale}><Save className="w-4 h-4 mr-1" />Salva</Button>
              <Button variant="outline" onClick={() => setShowAdd(false)} className="border-zinc-700 text-zinc-400" size="sm">Annulla</Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setShowAdd(true)} variant="outline" className="w-full mt-3 border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-emerald-500/50" size="sm" data-testid="add-soggetto-btn">
            <Plus className="w-4 h-4 mr-1" />Aggiungi Nuovo {tipo === "trasportatore" ? "Trasportatore" : "Destinatario"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard Component ────────────────────────────────────────
const CHART_COLORS = {
  trasmesso: "#34d399",
  in_attesa: "#fbbf24",
  errore: "#f87171",
  bozza: "#71717a",
};

const CHART_LABELS = {
  trasmesso: "Trasmessi",
  in_attesa: "In Attesa",
  errore: "Errore",
  bozza: "Bozza",
};

function DashboardTab({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    const data = await api("/dashboard/stats");
    if (data) setStats(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!stats) return <p className="text-zinc-500 text-center py-10">Errore nel caricamento dati</p>;

  const { movimenti, quantita, mesi, top_cer, azioni_richieste, fir, connessione } = stats;

  const pieData = [
    { name: "Trasmessi", value: movimenti.trasmessi, fill: CHART_COLORS.trasmesso },
    { name: "In Attesa", value: movimenti.in_attesa, fill: CHART_COLORS.in_attesa },
    { name: "Errore", value: movimenti.errori, fill: CHART_COLORS.errore },
    { name: "Bozza", value: movimenti.bozze, fill: CHART_COLORS.bozza },
  ].filter(d => d.value > 0);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-white text-sm font-medium">{payload[0].name || payload[0].payload?.name}</p>
        <p className="text-zinc-400 text-xs">{payload[0].value} movimenti</p>
      </div>
    );
  };

  const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-white text-sm font-semibold mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-xs" style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="rentri-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800" data-testid="kpi-total">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-lg bg-emerald-500/15"><Package className="w-5 h-5 text-emerald-400" /></div>
              {connessione?.is_connected && <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />}
            </div>
            <p className="text-3xl font-bold text-white tracking-tight">{movimenti.totale}</p>
            <p className="text-zinc-500 text-sm mt-1">Movimenti Totali</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800" data-testid="kpi-compliance">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-lg bg-emerald-500/15"><Target className="w-5 h-5 text-emerald-400" /></div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${movimenti.compliance_rate >= 80 ? "bg-emerald-500/15 text-emerald-400" : movimenti.compliance_rate >= 50 ? "bg-yellow-500/15 text-yellow-400" : "bg-red-500/15 text-red-400"}`}>
                {movimenti.compliance_rate >= 80 ? "Ottimo" : movimenti.compliance_rate >= 50 ? "Medio" : "Basso"}
              </span>
            </div>
            <p className="text-3xl font-bold text-white tracking-tight">{movimenti.compliance_rate}%</p>
            <p className="text-zinc-500 text-sm mt-1">Tasso Conformita</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800" data-testid="kpi-kg">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-lg bg-blue-500/15"><Scale className="w-5 h-5 text-blue-400" /></div>
            </div>
            <p className="text-3xl font-bold text-white tracking-tight">{quantita.kg >= 1000 ? `${(quantita.kg / 1000).toFixed(1)}t` : `${quantita.kg}`}</p>
            <p className="text-zinc-500 text-sm mt-1">Kg Gestiti</p>
            {quantita.litri > 0 && <p className="text-zinc-600 text-xs mt-0.5">+ {quantita.litri} litri</p>}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800" data-testid="kpi-actions">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2.5 rounded-lg ${(movimenti.errori + movimenti.in_attesa) > 0 ? "bg-amber-500/15" : "bg-emerald-500/15"}`}>
                <AlertTriangle className={`w-5 h-5 ${(movimenti.errori + movimenti.in_attesa) > 0 ? "text-amber-400" : "text-emerald-400"}`} />
              </div>
            </div>
            <p className="text-3xl font-bold text-white tracking-tight">{movimenti.errori + movimenti.in_attesa}</p>
            <p className="text-zinc-500 text-sm mt-1">Azioni Richieste</p>
            {movimenti.errori > 0 && <p className="text-red-400 text-xs mt-0.5">{movimenti.errori} errori</p>}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Donut Chart - Status Distribution */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="chart-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-400" />Distribuzione Stato
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-zinc-600 text-sm">Nessun movimento</div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-44 h-44 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value" stroke="none">
                        {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2.5 flex-1">
                  {[
                    { key: "trasmesso", val: movimenti.trasmessi },
                    { key: "in_attesa", val: movimenti.in_attesa },
                    { key: "errore", val: movimenti.errori },
                    { key: "bozza", val: movimenti.bozze },
                  ].map(s => (
                    <div key={s.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[s.key] }} />
                        <span className="text-zinc-400 text-sm">{CHART_LABELS[s.key]}</span>
                      </div>
                      <span className="text-white font-semibold text-sm">{s.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bar Chart - Monthly Trend */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-3" data-testid="chart-monthly">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />Andamento Mensile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mesi} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                  <Bar dataKey="carichi" name="Carichi" fill="#34d399" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  <Bar dataKey="scarichi" name="Scarichi" fill="#60a5fa" radius={[4, 4, 0, 0]} maxBarSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Actions Required */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="actions-required">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />Azioni Richieste
              </CardTitle>
              {azioni_richieste.length > 0 && (
                <Button variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs h-7"
                  onClick={() => onNavigate("registri")} data-testid="go-to-registri">
                  Vai al Registro <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {azioni_richieste.length === 0 ? (
              <div className="flex items-center gap-3 py-6 justify-center text-zinc-600">
                <CheckCircle className="w-5 h-5 text-emerald-500/50" />
                <span className="text-sm">Tutto in regola - nessuna azione richiesta</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto">
                {azioni_richieste.map((a, i) => (
                  <div key={a.id || i} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600/50 transition-all" data-testid={`action-item-${i}`}>
                    <div className={`w-2 h-8 rounded-full flex-shrink-0 ${a.stato === "errore" ? "bg-red-500" : "bg-yellow-500"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        #{a.numero_progressivo} - {a.codice_cer} {a.descrizione_rifiuto && `(${a.descrizione_rifiuto})`}
                      </p>
                      <p className="text-zinc-500 text-xs truncate">{a.tipo} - {a.last_error || (a.stato === "in_attesa" ? "In attesa di trasmissione" : "Errore")}</p>
                    </div>
                    <StatoLabel stato={a.stato} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Side Panel: Top CER + FIR Summary */}
        <div className="space-y-6">
          {/* Top CER Codes */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="top-cer">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Recycle className="w-4 h-4 text-emerald-400" />Codici CER Frequenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              {top_cer.length === 0 ? (
                <p className="text-zinc-600 text-sm text-center py-3">Nessun dato</p>
              ) : (
                <div className="space-y-3">
                  {top_cer.map((c, i) => {
                    const maxCount = top_cer[0]?.count || 1;
                    const pct = Math.round((c.count / maxCount) * 100);
                    return (
                      <div key={c.codice || i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm font-mono">{c.codice}</span>
                          <span className="text-zinc-500 text-xs">{c.count}x - {c.quantita} kg</span>
                        </div>
                        <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* FIR Summary */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="fir-summary">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-cyan-400" />FIR Digitali
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 text-xs h-7"
                  onClick={() => onNavigate("fir")} data-testid="go-to-fir">
                  Apri <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Totali", val: fir.totale, color: "text-white" },
                  { label: "Emessi", val: fir.emessi, color: "text-cyan-400" },
                  { label: "Bozze", val: fir.bozze, color: "text-zinc-400" },
                  { label: "Errori", val: fir.errori, color: "text-red-400" },
                ].map(s => (
                  <div key={s.label} className="p-2.5 rounded-lg bg-zinc-800/50 text-center">
                    <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                    <p className="text-zinc-500 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Movimento Form Modal ──────────────────────────────────────
function MovimentoFormModal({ open, onClose, onSaved, cerCodes }) {
  const [form, setForm] = useState({
    tipo: "SCARICO", data_operazione: new Date().toLocaleDateString("it-IT"), codice_cer: "",
    descrizione_rifiuto: "", quantita: "", unita_misura: "kg", causale_operazione: "",
    destinatario_nome: "", destinatario_cf: "", destinatario_num_autorizzazione: "",
    trasportatore_nome: "", trasportatore_cf: "", trasportatore_num_autorizzazione: "", note: "",
  });
  const [soggettiModal, setSoggettiModal] = useState(null);
  const [loading, setLoading] = useState(false);

  const CAUSALI_CARICO = [
    { code: "DT", label: "Prodotto/Detenuto nell'unita locale" },
    { code: "NP", label: "Nuovo produttore" },
    { code: "T*", label: "Ricevuto da terzi" },
    { code: "RE", label: "Residuo di produzione" },
    { code: "TR", label: "Trasferimento registri" },
  ];
  const CAUSALI_SCARICO = [
    { code: "aT", label: "Scarico a terzi" },
    { code: "I", label: "Scarico interno" },
    { code: "T*aT", label: "Ricevuto e scaricato a terzi" },
    { code: "M", label: "Movimentazione interna" },
  ];
  const causali = form.tipo === "CARICO" ? CAUSALI_CARICO : CAUSALI_SCARICO;

  const handleCerSelect = (cer) => {
    setForm(p => ({ ...p, codice_cer: cer.codice, descrizione_rifiuto: cer.descrizione }));
  };

  const handleSubmit = async () => {
    if (!form.codice_cer || !form.quantita) { toast.error("Compila codice CER e quantita"); return; }
    if (!form.causale_operazione) { toast.error("Seleziona una causale operazione"); return; }
    if (form.tipo === "SCARICO") {
      if (!form.destinatario_nome || !form.destinatario_cf) { toast.error("Destinatario e Codice Fiscale obbligatori per scarico"); return; }
      if (!form.trasportatore_nome || !form.trasportatore_cf) { toast.error("Trasportatore e Codice Fiscale obbligatori per scarico"); return; }
    }
    setLoading(true);
    const res = await api("/movimenti", { method: "POST", body: JSON.stringify({ ...form, quantita: parseFloat(form.quantita) }) });
    setLoading(false);
    if (res && res.id) { toast.success("Movimento creato"); onSaved(); onClose(); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="movimento-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-semibold text-lg">Nuovo Movimento</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          {/* Tipo */}
          <div className="flex gap-2">
            {["CARICO", "SCARICO"].map(t => (
              <button key={t} onClick={() => setForm(p => ({ ...p, tipo: t, causale_operazione: "" }))} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${form.tipo === t ? (t === "CARICO" ? "bg-blue-600 text-white" : "bg-orange-600 text-white") : "bg-zinc-800 text-zinc-400 border border-zinc-700"}`} data-testid={`tipo-${t.toLowerCase()}-btn`}>
                {t}
              </button>
            ))}
          </div>

          {/* Causale Operazione */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Causale Operazione *</label>
            <div className="grid grid-cols-2 gap-1.5">
              {causali.map(c => (
                <button key={c.code} onClick={() => setForm(p => ({ ...p, causale_operazione: c.code }))}
                  className={`px-3 py-2 rounded-lg text-xs text-left transition-all ${form.causale_operazione === c.code ? "bg-emerald-600/20 border-emerald-500/50 text-emerald-300 border" : "bg-zinc-800/60 border border-zinc-700/50 text-zinc-400 hover:border-zinc-600"}`}
                  data-testid={`causale-${c.code}-btn`}>
                  <span className="font-mono font-semibold">{c.code}</span> <span className="text-zinc-500">-</span> {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Data Operazione</label>
              <input value={form.data_operazione} onChange={e => setForm(p => ({ ...p, data_operazione: e.target.value }))} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="data-operazione-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Unita di Misura</label>
              <select value={form.unita_misura} onChange={e => setForm(p => ({ ...p, unita_misura: e.target.value }))} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500">
                <option value="kg">kg</option><option value="L">Litri</option><option value="m3">m3</option><option value="t">Tonnellate</option>
              </select>
            </div>
          </div>

          {/* CER code selector */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Codice CER</label>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-lg bg-zinc-800/50 border border-zinc-700">
              {cerCodes.map(cer => (
                <button key={cer.codice} onClick={() => handleCerSelect(cer)}
                  className={`text-xs px-2.5 py-1.5 rounded-md transition-all ${form.codice_cer === cer.codice ? "bg-emerald-600 text-white" : "bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700"} ${cer.pericoloso ? "border border-red-500/30" : ""}`}>
                  {cer.codice}{cer.pericoloso ? "*" : ""}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Descrizione Rifiuto</label>
            <input value={form.descrizione_rifiuto} onChange={e => setForm(p => ({ ...p, descrizione_rifiuto: e.target.value }))} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="descrizione-rifiuto-input" />
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Quantita</label>
            <input type="number" value={form.quantita} onChange={e => setForm(p => ({ ...p, quantita: e.target.value }))} placeholder="0" className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="quantita-input" />
          </div>

          {form.tipo === "SCARICO" && (
            <>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Destinatario *</label>
                <div className="flex gap-2">
                  <input value={form.destinatario_nome} onChange={e => setForm(p => ({ ...p, destinatario_nome: e.target.value }))} placeholder="Ragione sociale destinatario" className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="destinatario-nome-input" />
                  <Button variant="outline" className="border-zinc-700 text-zinc-400" size="sm" onClick={() => setSoggettiModal("destinatario")}><Users className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <input value={form.destinatario_cf} onChange={e => setForm(p => ({ ...p, destinatario_cf: e.target.value.toUpperCase() }))} placeholder="C.F. / P.IVA *" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500 uppercase" maxLength={16} data-testid="destinatario-cf-input" />
                  <input value={form.destinatario_num_autorizzazione} onChange={e => setForm(p => ({ ...p, destinatario_num_autorizzazione: e.target.value }))} placeholder="N. Autorizzazione *" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="destinatario-auth-input" />
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Trasportatore *</label>
                <div className="flex gap-2">
                  <input value={form.trasportatore_nome} onChange={e => setForm(p => ({ ...p, trasportatore_nome: e.target.value }))} placeholder="Ragione sociale trasportatore" className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="trasportatore-nome-input" />
                  <Button variant="outline" className="border-zinc-700 text-zinc-400" size="sm" onClick={() => setSoggettiModal("trasportatore")}><Users className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <input value={form.trasportatore_cf} onChange={e => setForm(p => ({ ...p, trasportatore_cf: e.target.value.toUpperCase() }))} placeholder="C.F. / P.IVA *" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500 uppercase" maxLength={16} data-testid="trasportatore-cf-input" />
                  <input value={form.trasportatore_num_autorizzazione} onChange={e => setForm(p => ({ ...p, trasportatore_num_autorizzazione: e.target.value }))} placeholder="N. Autorizzazione" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="trasportatore-auth-input" />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Note</label>
            <textarea value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} rows={2} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500 resize-none" />
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-movimento-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Salva Movimento
            </Button>
            <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-400">Annulla</Button>
          </div>
        </div>

        <SoggettiModal open={!!soggettiModal} onClose={() => setSoggettiModal(null)} tipo={soggettiModal || "destinatario"} onSelect={(s) => {
          if (soggettiModal === "destinatario") setForm(p => ({ ...p, destinatario_nome: s.ragione_sociale, destinatario_id: s.id }));
          else setForm(p => ({ ...p, trasportatore_nome: s.ragione_sociale, trasportatore_id: s.id }));
        }} />
      </div>
    </div>
  );
}

// ─── FIR Form Modal ──────────────────────────────────────────────
function FirFormModal({ open, onClose, onSaved, cerCodes }) {
  const [form, setForm] = useState({
    data_emissione: new Date().toISOString().slice(0, 10), codice_cer: "", descrizione_rifiuto: "",
    quantita: "", unita_misura: "kg", codice_blocco: "", provenienza: "S",
    produttore_nome: "", produttore_cf: "", produttore_indirizzo: "",
    produttore_cap: "", produttore_citta_codice: "", produttore_citta_nome: "", produttore_provincia: "",
    trasportatore_nome: "", trasportatore_cf: "", trasportatore_targa: "",
    conducente_nome: "", conducente_cognome: "",
    destinatario_nome: "", destinatario_cf: "", destinatario_indirizzo: "",
    destinatario_cap: "", destinatario_citta_codice: "", destinatario_citta_nome: "", destinatario_provincia: "",
    note: "",
  });
  const [soggettiModal, setSoggettiModal] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCerSelect = (cer) => setForm(p => ({ ...p, codice_cer: cer.codice, descrizione_rifiuto: cer.descrizione }));

  const handleSubmit = async () => {
    if (!form.codice_cer || !form.quantita) { toast.error("Compila codice CER e quantita"); return; }
    setLoading(true);
    const res = await api("/fir", { method: "POST", body: JSON.stringify({ ...form, quantita: parseFloat(form.quantita) }) });
    setLoading(false);
    if (res && res.id) { toast.success(`FIR ${res.numero_fir} creato`); onSaved(); onClose(); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="fir-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-auto p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-semibold text-lg">Nuovo FIR Digitale</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Data Emissione</label>
              <input value={form.data_emissione} onChange={e => setForm(p => ({ ...p, data_emissione: e.target.value }))} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Unita di Misura</label>
              <select value={form.unita_misura} onChange={e => setForm(p => ({ ...p, unita_misura: e.target.value }))} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500">
                <option value="kg">kg</option><option value="L">Litri</option><option value="m3">m3</option><option value="t">Tonnellate</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Codice CER</label>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-2 rounded-lg bg-zinc-800/50 border border-zinc-700">
              {cerCodes.map(cer => (
                <button key={cer.codice} onClick={() => handleCerSelect(cer)}
                  className={`text-xs px-2.5 py-1.5 rounded-md transition-all ${form.codice_cer === cer.codice ? "bg-emerald-600 text-white" : "bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700"} ${cer.pericoloso ? "border border-red-500/30" : ""}`}>
                  {cer.codice}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Descrizione Rifiuto</label>
              <input value={form.descrizione_rifiuto} onChange={e => setForm(p => ({ ...p, descrizione_rifiuto: e.target.value }))} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Quantita</label>
              <input type="number" value={form.quantita} onChange={e => setForm(p => ({ ...p, quantita: e.target.value }))} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
          </div>

          {/* Produttore */}
          <div className="p-3 rounded-lg border border-zinc-700/50 bg-zinc-800/30 space-y-2">
            <p className="text-xs text-zinc-400 font-medium flex items-center gap-1"><Factory className="w-3.5 h-3.5" />Produttore</p>
            <div className="grid grid-cols-3 gap-2">
              <input value={form.produttore_nome} onChange={e => setForm(p => ({ ...p, produttore_nome: e.target.value }))} placeholder="Ragione Sociale" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.produttore_cf} onChange={e => setForm(p => ({ ...p, produttore_cf: e.target.value.toUpperCase() }))} placeholder="Codice Fiscale / P.IVA" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.produttore_indirizzo} onChange={e => setForm(p => ({ ...p, produttore_indirizzo: e.target.value }))} placeholder="Via e Civico" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <input value={form.produttore_cap} onChange={e => setForm(p => ({ ...p, produttore_cap: e.target.value }))} placeholder="CAP" maxLength={5} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.produttore_citta_nome} onChange={e => setForm(p => ({ ...p, produttore_citta_nome: e.target.value }))} placeholder="Citta" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.produttore_citta_codice} onChange={e => setForm(p => ({ ...p, produttore_citta_codice: e.target.value.toUpperCase() }))} placeholder="Cod. Catastale (es. B354)" maxLength={4} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.produttore_provincia} onChange={e => setForm(p => ({ ...p, produttore_provincia: e.target.value.toUpperCase() }))} placeholder="Prov. (es. CA)" maxLength={2} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
          </div>

          {/* Trasportatore */}
          <div className="p-3 rounded-lg border border-zinc-700/50 bg-zinc-800/30 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-400 font-medium flex items-center gap-1"><Truck className="w-3.5 h-3.5" />Trasportatore</p>
              <Button variant="ghost" size="sm" className="h-6 text-xs text-emerald-400" onClick={() => setSoggettiModal("trasportatore")}><Users className="w-3 h-3 mr-1" />Rubrica</Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input value={form.trasportatore_nome} onChange={e => setForm(p => ({ ...p, trasportatore_nome: e.target.value }))} placeholder="Ragione Sociale" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.trasportatore_cf} onChange={e => setForm(p => ({ ...p, trasportatore_cf: e.target.value.toUpperCase() }))} placeholder="C.F. / P.IVA" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.trasportatore_targa} onChange={e => setForm(p => ({ ...p, trasportatore_targa: e.target.value.toUpperCase() }))} placeholder="Targa Mezzo" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={form.conducente_nome} onChange={e => setForm(p => ({ ...p, conducente_nome: e.target.value }))} placeholder="Nome Conducente" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.conducente_cognome} onChange={e => setForm(p => ({ ...p, conducente_cognome: e.target.value }))} placeholder="Cognome Conducente" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
          </div>

          {/* Destinatario */}
          <div className="p-3 rounded-lg border border-zinc-700/50 bg-zinc-800/30 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-400 font-medium flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />Destinatario</p>
              <Button variant="ghost" size="sm" className="h-6 text-xs text-emerald-400" onClick={() => setSoggettiModal("destinatario")}><Users className="w-3 h-3 mr-1" />Rubrica</Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input value={form.destinatario_nome} onChange={e => setForm(p => ({ ...p, destinatario_nome: e.target.value }))} placeholder="Ragione Sociale" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.destinatario_cf} onChange={e => setForm(p => ({ ...p, destinatario_cf: e.target.value.toUpperCase() }))} placeholder="C.F. / P.IVA" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.destinatario_indirizzo} onChange={e => setForm(p => ({ ...p, destinatario_indirizzo: e.target.value }))} placeholder="Via e Civico" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <input value={form.destinatario_cap} onChange={e => setForm(p => ({ ...p, destinatario_cap: e.target.value }))} placeholder="CAP" maxLength={5} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.destinatario_citta_nome} onChange={e => setForm(p => ({ ...p, destinatario_citta_nome: e.target.value }))} placeholder="Citta" className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.destinatario_citta_codice} onChange={e => setForm(p => ({ ...p, destinatario_citta_codice: e.target.value.toUpperCase() }))} placeholder="Cod. Catastale (es. F205)" maxLength={4} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
              <input value={form.destinatario_provincia} onChange={e => setForm(p => ({ ...p, destinatario_provincia: e.target.value.toUpperCase() }))} placeholder="Prov. (es. MI)" maxLength={2} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" />
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Note</label>
            <textarea value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} rows={2} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500 resize-none" />
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Codice Blocco FIR (opzionale - dal portale RENTRI)</label>
            <input value={form.codice_blocco} onChange={e => setForm(p => ({ ...p, codice_blocco: e.target.value }))} placeholder="Es. BLK-2026-001" className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm outline-none focus:border-emerald-500" data-testid="fir-codice-blocco" />
            <p className="text-xs text-zinc-600 mt-1">Se non specificato, verra cercato automaticamente dal portale RENTRI</p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-fir-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck className="w-4 h-4 mr-2" />}Crea FIR
            </Button>
            <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-400">Annulla</Button>
          </div>
        </div>

        <SoggettiModal open={!!soggettiModal} onClose={() => setSoggettiModal(null)} tipo={soggettiModal || "destinatario"} onSelect={(s) => {
          if (soggettiModal === "destinatario") setForm(p => ({ ...p, destinatario_nome: s.ragione_sociale, destinatario_cf: s.codice_fiscale, destinatario_indirizzo: s.indirizzo, destinatario_id: s.id }));
          else setForm(p => ({ ...p, trasportatore_nome: s.ragione_sociale, trasportatore_cf: s.codice_fiscale, trasportatore_id: s.id }));
        }} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════
export default function Rentri({ user }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [config, setConfig] = useState(null);
  const [cerCodes, setCerCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState("tutti");

  // Movimenti state
  const [movimenti, setMovimenti] = useState([]);
  const [movStats, setMovStats] = useState({});
  const [showMovForm, setShowMovForm] = useState(false);

  // FIR state
  const [firList, setFirList] = useState([]);
  const [firStats, setFirStats] = useState({});
  const [showFirForm, setShowFirForm] = useState(false);
  const [firSearch, setFirSearch] = useState("");

  // Config state
  const [certFile, setCertFile] = useState(null);
  const [certPassword, setCertPassword] = useState("");
  const [numIscrizione, setNumIscrizione] = useState("");
  const [numIscrSito, setNumIscrSito] = useState("");
  const [environment, setEnvironment] = useState("demo");
  const [testingConn, setTestingConn] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connResult, setConnResult] = useState(null);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [statusAlerts, setStatusAlerts] = useState([]);

  // Load initial data
  const loadConfig = useCallback(async () => {
    const c = await api("/config");
    if (c) {
      setConfig(c);
      setNumIscrizione(c.numero_iscrizione || "");
      setNumIscrSito(c.num_iscr_sito || "");
      setEnvironment(c.environment || "demo");
    }
  }, []);

  const loadMovimenti = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterTipo !== "tutti") params.set("tipo", filterTipo);
    if (searchTerm) params.set("search", searchTerm);
    const [data, stats] = await Promise.all([
      api(`/movimenti?${params}`),
      api("/movimenti/stats"),
    ]);
    if (data) setMovimenti(data.items || []);
    if (stats) setMovStats(stats);
  }, [filterTipo, searchTerm]);

  const loadFir = useCallback(async () => {
    const params = new URLSearchParams();
    if (firSearch) params.set("search", firSearch);
    const [data, stats] = await Promise.all([
      api(`/fir?${params}`),
      api("/fir/stats"),
    ]);
    if (data) setFirList(data.items || []);
    if (stats) setFirStats(stats);
  }, [firSearch]);

  useEffect(() => { loadConfig(); api("/cer-codes").then(d => d && setCerCodes(d)); }, [loadConfig]);
  useEffect(() => { if (activeTab === "registri") loadMovimenti(); }, [activeTab, loadMovimenti]);
  useEffect(() => { if (activeTab === "fir") loadFir(); }, [activeTab, loadFir]);

  // Load notifications and status alerts
  const loadNotifications = useCallback(async () => {
    const [notifs, count, status] = await Promise.all([
      api("/notifications?limit=20"),
      api("/notifications/unread-count"),
      api("/status-check"),
    ]);
    if (notifs) setNotifications(notifs);
    if (count) setUnreadCount(count.count || 0);
    if (status) setStatusAlerts(status.alerts || []);
  }, []);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);
  // Poll every 30s for new notifications
  useEffect(() => {
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Handlers
  const handleUploadCert = async () => {
    if (!certFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", certFile);
    try {
      const res = await authFetch(`${API}/api/rentri/config/upload-cert`, {
        method: "POST", body: formData,
      });
      const data = await res.json();
      if (data.success) { toast.success("Certificato caricato"); loadConfig(); }
      else toast.error(data.detail || "Errore upload");
    } catch { toast.error("Errore upload certificato"); }
    setUploading(false);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    const res = await api("/config", {
      method: "PUT",
      body: JSON.stringify({ environment, numero_iscrizione: numIscrizione, num_iscr_sito: numIscrSito, cert_password: certPassword || undefined }),
    });
    setSaving(false);
    if (res) { setConfig(res); toast.success("Configurazione salvata"); }
  };

  const handleTestConnection = async () => {
    setTestingConn(true);
    setConnResult(null);
    try {
      const res = await api("/config/test-connection", { method: "POST" });
      setConnResult(res);
      if (res && res.success) {
        if (res.api_status_code === 200 || res.api_status_code === 204) {
          toast.success("Connessione autenticata con successo!");
        } else if (res.api_status_code === 401) {
          toast.warning("Certificato non accettato dal portale RENTRI. Verifica attivazione interoperabilita.");
        } else {
          toast.info(res.api_message || "Connessione testata");
        }
        loadConfig();
      } else {
        toast.error(res?.detail || "Errore connessione");
      }
    } catch { toast.error("Errore test connessione"); }
    setTestingConn(false);
  };

  const handleTrasmetti = async (movId) => {
    toast.info("Trasmissione in corso...");
    const res = await api(`/movimenti/${movId}/trasmetti`, { method: "POST" });
    if (res && res.success !== false) {
      toast.success(res.message || `Stato: ${res.stato}`);
    } else if (res) {
      toast.error(res.message || "Errore nella trasmissione");
    } else {
      toast.error("Errore di comunicazione con il server");
    }
    loadMovimenti();
    loadNotifications();
  };

  const handleDeleteMov = async (movId) => {
    const res = await api(`/movimenti/${movId}`, { method: "DELETE" });
    if (res && res.success) { toast.success("Movimento eliminato"); loadMovimenti(); loadNotifications(); }
  };

  const handleEmettiFir = async (firId) => {
    toast.info("Invio FIR in corso...");
    const res = await api(`/fir/${firId}/emetti`, { method: "POST" });
    if (res && res.success) {
      if (res.trasmesso === false) {
        toast.warning(res.message || "FIR vidimato ma dati non trasmessi al portale");
      } else {
        toast.success(res.message || "FIR emesso e trasmesso con successo");
      }
    } else if (res && !res.success) {
      toast.error(res.message || "Errore nell'emissione del FIR");
    } else {
      toast.error("Errore di comunicazione con il server");
    }
    loadFir();
    loadNotifications();
  };

  const handleRitrasmettiFir = async (firId) => {
    toast.info("Ritrasmissione dati FIR in corso...");
    const res = await api(`/fir/${firId}/ritrasmetti`, { method: "POST" });
    if (res && res.success) {
      toast.success(res.message || "Dati FIR trasmessi con successo");
    } else if (res) {
      toast.error(res.message || "Errore nella ritrasmissione");
    } else {
      toast.error("Errore di comunicazione con il server");
    }
    loadFir();
  };

  const handleDeleteFir = async (firId) => {
    const res = await api(`/fir/${firId}`, { method: "DELETE" });
    if (res && res.success) { toast.success("FIR eliminato"); loadFir(); loadNotifications(); }
  };

  const handleTrasmettiTutti = async () => {
    const res = await api("/movimenti/trasmetti-tutti", { method: "POST" });
    if (res) { toast.success(`${res.trasmessi} movimenti trasmessi`); loadMovimenti(); loadNotifications(); }
  };

  const handleMarkAllRead = async () => {
    await api("/notifications/read-all", { method: "PATCH" });
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const isConnected = config?.is_connected;
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "config", label: "Configurazione", icon: Settings },
    { id: "registri", label: "Registro Rifiuti", icon: Recycle },
    { id: "fir", label: "FIR Digitali", icon: FileCheck },
  ];

  // Check if RENTRI module is enabled for this user (admins always have access)
  const isAdminUser = user?.role?.toUpperCase() === "ADMIN";
  if (!isAdminUser && user && user.rentri_enabled === false) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <Lock className="w-12 h-12 text-zinc-600 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Modulo RENTRI non attivo</h2>
        <p className="text-zinc-500">Contatta l'amministratore per abilitare il modulo RENTRI sul tuo account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="rentri-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-emerald-500/20 border border-emerald-500/30">
            <Recycle className="w-7 h-7 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">RENTRI - Tracciabilita Rifiuti</h1>
            <p className="text-zinc-400 mt-0.5 text-sm">Registro Elettronico Nazionale per la Tracciabilita dei Rifiuti</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Notification Bell */}
          <div className="relative">
            <button onClick={() => setShowNotifs(!showNotifs)} className={`p-2.5 rounded-lg border transition-all ${unreadCount > 0 ? "bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-700"}`} data-testid="rentri-notifications-bell">
              {unreadCount > 0 ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
              {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unreadCount > 9 ? "9+" : unreadCount}</span>}
            </button>

            {/* Notification Panel */}
            {showNotifs && (
              <div className="absolute right-0 top-12 w-96 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl shadow-black/40 z-50 overflow-hidden" data-testid="rentri-notifications-panel">
                <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                  <h3 className="text-white font-semibold text-sm">Notifiche RENTRI</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-xs text-emerald-400 hover:text-emerald-300">Segna tutte lette</button>}
                    <button onClick={() => setShowNotifs(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                </div>

                {/* Status Alerts */}
                {statusAlerts.length > 0 && (
                  <div className="p-3 border-b border-zinc-800 space-y-2">
                    {statusAlerts.map((alert, i) => (
                      <div key={i} className={`flex items-center gap-2.5 p-2.5 rounded-lg text-xs ${alert.type === "errore" ? "bg-red-500/10 border border-red-500/20" : "bg-yellow-500/10 border border-yellow-500/20"}`}>
                        {alert.type === "errore" ? <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" /> : <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" />}
                        <span className={`${alert.type === "errore" ? "text-red-300" : "text-yellow-300"}`}>{alert.message}</span>
                        <button onClick={() => { setActiveTab(alert.category === "fir" ? "fir" : "registri"); setShowNotifs(false); }} className="ml-auto text-emerald-400 hover:text-emerald-300 font-medium flex-shrink-0">Vai</button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-zinc-500 text-sm text-center py-8">Nessuna notifica</p>
                  ) : (
                    notifications.map(n => (
                      <div key={n.notification_id} className={`p-3.5 border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${!n.read ? "bg-emerald-500/5 border-l-2 border-l-emerald-500" : ""}`}>
                        <div className="flex items-start gap-2.5">
                          <div className={`mt-0.5 p-1 rounded ${n.type?.includes("errore") ? "bg-red-500/20" : n.type?.includes("ok") || n.type?.includes("emesso") ? "bg-emerald-500/20" : "bg-blue-500/20"}`}>
                            {n.type?.includes("errore") ? <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> : n.type?.includes("ok") || n.type?.includes("emesso") ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Info className="w-3.5 h-3.5 text-blue-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium">{n.title}</p>
                            <p className="text-zinc-400 text-xs mt-0.5 line-clamp-2">{n.message}</p>
                            <p className="text-zinc-600 text-[10px] mt-1">{n.created_at ? new Date(n.created_at).toLocaleString("it-IT") : ""}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${isConnected ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-zinc-800 border-zinc-700 text-zinc-400"}`} data-testid="connection-status">
            {isConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            <span className="text-sm font-medium">{isConnected ? "Connesso" : "Non connesso"}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 p-1 rounded-lg border border-zinc-800 w-fit">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/20" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`}
            data-testid={`rentri-tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: Dashboard ═══ */}
      {activeTab === "dashboard" && (
        <DashboardTab onNavigate={setActiveTab} />
      )}

      {/* ═══ TAB: Configurazione ═══ */}
      {activeTab === "config" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Certificato */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Shield className="w-5 h-5 text-emerald-400" />Certificato di Interoperabilita</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div className="p-5 rounded-xl border-2 border-dashed border-zinc-700 hover:border-emerald-500/50 transition-colors bg-zinc-800/30">
                  <div className="text-center">
                    {config?.cert_uploaded ? (
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <CheckCircle className="w-6 h-6 text-emerald-400" />
                        <span className="text-emerald-400 font-medium">{config.cert_filename}</span>
                      </div>
                    ) : (
                      <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                    )}
                    <p className="text-white font-medium">Carica Certificato Digitale (.p12 / .pfx)</p>
                    <p className="text-zinc-500 text-sm mt-1.5">Scarica il certificato dal portale RENTRI &gt; Interoperabilita &gt; Emissione certificati</p>
                    <div className="flex items-center justify-center gap-3 mt-4">
                      <label className="cursor-pointer">
                        <input type="file" accept=".p12,.pfx" onChange={e => setCertFile(e.target.files[0])} className="hidden" data-testid="cert-file-input" />
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg transition-colors">
                          <Upload className="w-4 h-4" />Seleziona File
                        </span>
                      </label>
                      {certFile && (
                        <Button onClick={handleUploadCert} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="upload-cert-btn">
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Carica"}
                        </Button>
                      )}
                    </div>
                    {certFile && <p className="text-zinc-400 text-xs mt-2">File selezionato: {certFile.name}</p>}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-zinc-400 font-medium block mb-1.5">Password Certificato</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input type="password" value={certPassword} onChange={e => setCertPassword(e.target.value)} placeholder="Inserisci la password del certificato" className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none" data-testid="cert-password-input" />
                    </div>
                    {config?.cert_password_set && <p className="text-emerald-400 text-xs mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Password salvata</p>}
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 font-medium block mb-1.5">Numero Iscrizione RENTRI</label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input type="text" value={numIscrizione} onChange={e => setNumIscrizione(e.target.value)} placeholder="Es: RSZGW6W1J7A" className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none" data-testid="rentri-number-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 font-medium block mb-1.5">Identificativo Unita Locale (num_iscr_sito)</label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input type="text" value={numIscrSito} onChange={e => setNumIscrSito(e.target.value)} placeholder="Es: OP260355F082662-CA0001" className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none" data-testid="rentri-sito-input" />
                    </div>
                    <p className="text-xs text-zinc-600 mt-1">Visibile nel portale RENTRI sotto "Unita locale"</p>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button onClick={handleTestConnection} disabled={testingConn || !config?.cert_uploaded} className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1" data-testid="test-connection-btn">
                    {testingConn ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}Testa Connessione
                  </Button>
                  <Button onClick={handleSaveConfig} disabled={saving} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="save-config-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Salva
                  </Button>
                </div>

                {/* Connection test result */}
                {connResult && (
                  <div className={`p-4 rounded-lg border mt-3 ${connResult.api_status_code === 200 || connResult.api_status_code === 204 ? "border-emerald-500/30 bg-emerald-500/10" : connResult.api_reachable ? "border-yellow-500/30 bg-yellow-500/10" : "border-red-500/30 bg-red-500/10"}`} data-testid="conn-result-panel">
                    <p className={`text-sm font-medium ${connResult.api_status_code === 200 || connResult.api_status_code === 204 ? "text-emerald-400" : connResult.api_reachable ? "text-yellow-400" : "text-red-400"}`}>
                      {connResult.api_message}
                    </p>
                    {connResult.api_status_code === 401 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-zinc-300 text-xs font-medium">Possibili cause:</p>
                        <ul className="text-zinc-400 text-xs space-y-1 ml-4 list-disc">
                          <li>Verifica che l'interoperabilita sia attivata sul portale RENTRI</li>
                          <li>Controlla che il certificato sia stato generato per le API (sezione "Interoperabilita - Certificati")</li>
                          <li>Il certificato potrebbe necessitare di qualche minuto per l'attivazione</li>
                        </ul>
                        <a href="https://demo.rentri.gov.it" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-medium mt-2">
                          <ExternalLink className="w-3 h-3" />Vai al portale RENTRI Demo
                        </a>
                      </div>
                    )}
                    {connResult.api_status_code && <p className="text-zinc-500 text-xs mt-2">HTTP Status: {connResult.api_status_code}</p>}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ambiente */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-white flex items-center gap-2 text-base"><Building2 className="w-5 h-5 text-blue-400" />Ambiente di Connessione</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: "production", label: "Produzione", url: "api.rentri.gov.it", color: "emerald" },
                    { id: "demo", label: "Demo / Test", url: "demoapi.rentri.gov.it", color: "yellow" },
                  ].map(env => (
                    <button key={env.id} onClick={() => setEnvironment(env.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${environment === env.id ? `border-${env.color}-500/50 bg-${env.color}-500/10` : "border-zinc-700 bg-zinc-800/50 hover:border-zinc-600"}`}
                      data-testid={`env-${env.id}-btn`}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2.5 h-2.5 rounded-full bg-${env.color}-400`}></div>
                        <span className={`font-medium ${environment === env.id ? "text-white" : "text-zinc-300"}`}>{env.label}</span>
                      </div>
                      <p className="text-zinc-500 text-xs">{env.url}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cert info if available */}
            {config?.cert_info && (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader><CardTitle className="text-white flex items-center gap-2 text-base"><FileText className="w-5 h-5 text-cyan-400" />Informazioni Certificato</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(config.cert_info).filter(([,v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-zinc-500 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="text-zinc-300 font-mono text-xs max-w-[300px] truncate">{v}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-white text-base">Stato Integrazione</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: "Certificato", ok: config?.cert_uploaded },
                  { label: "Password", ok: config?.cert_password_set },
                  { label: "Connessione API", ok: config?.is_connected },
                  { label: "Registro Attivo", ok: config?.is_connected },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400">{item.label}</span>
                    {item.ok ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <div className="w-5 h-5 rounded-full border-2 border-zinc-700" />}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-white text-base">Guida Rapida</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[
                  "Accedi al portale RENTRI con SPID/CIE",
                  "Vai a Interoperabilita > Certificati",
                  "Scarica il certificato .p12",
                  "Caricalo qui e inserisci la password",
                  "Testa la connessione",
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="text-sm text-zinc-400">{text}</span>
                  </div>
                ))}
                <a href="https://www.rentri.gov.it" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 mt-3 font-medium">
                  <ExternalLink className="w-4 h-4" />Vai al Portale RENTRI
                </a>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-white text-base">Scadenze Normative</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-red-400 text-xs font-medium">OBBLIGO ATTIVO</p>
                  <p className="text-white text-sm mt-1">FIR digitale obbligatorio</p>
                  <p className="text-zinc-500 text-xs">Dal 13/02/2026</p>
                </div>
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-yellow-400 text-xs font-medium">TRANSITORIO</p>
                  <p className="text-white text-sm mt-1">FIR cartaceo ammesso</p>
                  <p className="text-zinc-500 text-xs">Fino al 15/09/2026</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ═══ TAB: Registro Rifiuti ═══ */}
      {activeTab === "registri" && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Movimenti Mese", value: movStats.mese_corrente || 0, icon: ArrowUpDown, color: "text-emerald-400", bg: "bg-emerald-500/15" },
              { label: "Carichi", value: movStats.carichi_mese || 0, icon: Plus, color: "text-blue-400", bg: "bg-blue-500/15" },
              { label: "Scarichi", value: movStats.scarichi_mese || 0, icon: Truck, color: "text-orange-400", bg: "bg-orange-500/15" },
              { label: "Da Trasmettere", value: movStats.da_trasmettere || 0, icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/15" },
            ].map((stat, i) => (
              <Card key={i} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">{stat.label}</p>
                      <p className={`text-2xl font-bold ${stat.color} mt-1`}>{stat.value}</p>
                    </div>
                    <div className={`p-2.5 rounded-lg ${stat.bg}`}><stat.icon className={`w-5 h-5 ${stat.color}`} /></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Actions bar */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input type="text" placeholder="Cerca per CER o descrizione..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:border-emerald-500 outline-none text-sm" data-testid="registri-search" />
                  </div>
                  <div className="flex items-center gap-1 bg-zinc-800 rounded-lg border border-zinc-700 p-0.5">
                    {["tutti", "CARICO", "SCARICO"].map(tipo => (
                      <button key={tipo} onClick={() => setFilterTipo(tipo)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filterTipo === tipo ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-white"}`} data-testid={`filter-${tipo}`}>
                        {tipo === "tutti" ? "Tutti" : tipo}
                      </button>
                    ))}
                  </div>
                </div>
                <Button onClick={() => setShowMovForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="new-movimento-btn">
                  <Plus className="w-4 h-4 mr-1.5" />Nuovo Movimento
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="registri-table">
                <thead>
                  <tr className="border-b border-zinc-800">
                    {["#", "Data", "Tipo", "CER", "Descrizione", "Quantita", "Destinatario", "Stato", "Azioni"].map(h => (
                      <th key={h} className="text-left text-xs text-zinc-500 uppercase tracking-wider font-medium px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movimenti.map(row => (
                    <tr key={row.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3 text-xs text-zinc-500">#{row.numero_progressivo}</td>
                      <td className="px-4 py-3 text-sm text-white">{row.data_operazione}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${row.tipo === "CARICO" ? "bg-blue-500/15 text-blue-400 border border-blue-500/30" : "bg-orange-500/15 text-orange-400 border border-orange-500/30"}`}>{row.tipo}</span>
                        {row.causale_operazione && <span className="ml-1.5 text-xs text-zinc-500 font-mono">{row.causale_operazione}</span>}
                      </td>
                      <td className="px-4 py-3"><code className="text-sm text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded font-mono">{row.codice_cer}</code></td>
                      <td className="px-4 py-3 text-sm text-zinc-300 max-w-[200px] truncate">{row.descrizione_rifiuto}</td>
                      <td className="px-4 py-3 text-sm text-white font-medium">{row.quantita} {row.unita_misura}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{row.destinatario_nome || "-"}</td>
                      <td className="px-4 py-3"><StatoLabel stato={row.stato} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {row.stato === "bozza" && (
                            <>
                              <button onClick={() => handleTrasmetti(row.id)} className="p-1.5 rounded hover:bg-emerald-500/20 text-zinc-500 hover:text-emerald-400 transition-colors" title="Trasmetti" data-testid={`trasmetti-${row.id}`}><Send className="w-4 h-4" /></button>
                              <button onClick={() => handleDeleteMov(row.id)} className="p-1.5 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-colors" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                            </>
                          )}
                          {row.stato === "errore" && (
                            <button onClick={() => handleTrasmetti(row.id)} className="p-1.5 rounded hover:bg-yellow-500/20 text-zinc-500 hover:text-yellow-400 transition-colors" title="Riprova"><RefreshCw className="w-4 h-4" /></button>
                          )}
                          {row.stato === "in_elaborazione" && (
                            <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {movimenti.length === 0 && (
                    <tr><td colSpan="9" className="text-center text-zinc-500 py-12">Nessun movimento registrato. Clicca "Nuovo Movimento" per iniziare.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Trasmetti tutti */}
          {(movStats.da_trasmettere || 0) > 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-white text-sm font-medium">Trasmissione Automatica</p>
                    <p className="text-zinc-500 text-xs">{movStats.da_trasmettere} movimenti da trasmettere</p>
                  </div>
                </div>
                <Button onClick={handleTrasmettiTutti} variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" size="sm" data-testid="trasmetti-tutti-btn">
                  <Send className="w-4 h-4 mr-1.5" />Trasmetti Tutti
                </Button>
              </CardContent>
            </Card>
          )}

          <MovimentoFormModal open={showMovForm} onClose={() => setShowMovForm(false)} onSaved={loadMovimenti} cerCodes={cerCodes} />
        </div>
      )}

      {/* ═══ TAB: FIR Digitali ═══ */}
      {activeTab === "fir" && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "FIR Totali", value: firStats.totale || 0, icon: FileCheck, color: "text-emerald-400", bg: "bg-emerald-500/15" },
              { label: "Completati", value: firStats.completati || 0, icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/15" },
              { label: "In Transito", value: firStats.in_transito || 0, icon: Truck, color: "text-blue-400", bg: "bg-blue-500/15" },
              { label: "Con Errori", value: firStats.errori || 0, icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/15" },
            ].map((stat, i) => (
              <Card key={i} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">{stat.label}</p>
                      <p className={`text-2xl font-bold ${stat.color} mt-1`}>{stat.value}</p>
                    </div>
                    <div className={`p-2.5 rounded-lg ${stat.bg}`}><stat.icon className={`w-5 h-5 ${stat.color}`} /></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input type="text" placeholder="Cerca FIR per numero, destinatario..." value={firSearch} onChange={e => setFirSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder:text-zinc-500 focus:border-emerald-500 outline-none text-sm" data-testid="fir-search" />
            </div>
            <Button onClick={() => setShowFirForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="new-fir-btn">
              <Plus className="w-4 h-4 mr-1.5" />Nuovo xFIR
            </Button>
          </div>

          {/* FIR Cards */}
          {firList.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {firList.map(fir => (
                <Card key={fir.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors group" data-testid={`fir-card-${fir.numero_fir}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <code className="text-emerald-400 font-mono font-bold text-sm">{fir.numero_fir}</code>
                          <StatoLabel stato={fir.stato} />
                          {fir.stato === "emesso" && fir.trasmesso === false && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/30">Non trasmesso</span>
                          )}
                          {fir.stato === "emesso" && fir.trasmesso === true && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-500/15 text-green-400 border border-green-500/30">Trasmesso</span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-xs mt-1">{fir.data_emissione} - CER {fir.codice_cer}</p>
                      </div>
                      <div className="flex gap-1">
                        {(fir.stato === "bozza" || fir.stato === "errore") && (
                          <>
                            <button onClick={() => handleEmettiFir(fir.id)} className="p-1.5 rounded hover:bg-emerald-500/20 text-zinc-500 hover:text-emerald-400" title="Emetti FIR" data-testid={`emit-fir-${fir.numero_fir}`}><Send className="w-4 h-4" /></button>
                            {fir.stato === "bozza" && (
                              <button onClick={() => handleDeleteFir(fir.id)} className="p-1.5 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </>
                        )}
                        {fir.stato === "in_elaborazione" && (
                          <div className="flex items-center gap-1.5 text-amber-400">
                            <Loader2 className="w-4 h-4 animate-spin" /><span className="text-xs">Elaborazione...</span>
                          </div>
                        )}
                        {fir.stato === "emesso" && fir.trasmesso === false && (
                          <button onClick={() => handleRitrasmettiFir(fir.id)} className="p-1.5 rounded hover:bg-emerald-500/20 text-zinc-500 hover:text-emerald-400" title="Ritrasmetti dati al portale RENTRI" data-testid={`retransmit-fir-${fir.numero_fir}`}>
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Flow visualization */}
                    <div className="flex items-center gap-2 text-xs">
                      <div className="flex-1 p-2.5 rounded-lg bg-zinc-800 border border-zinc-700">
                        <div className="flex items-center gap-1.5 text-zinc-500 mb-0.5"><Factory className="w-3 h-3" /><span>Produttore</span></div>
                        <span className="text-white font-medium text-xs">{fir.produttore_nome || "-"}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                      <div className="flex-1 p-2.5 rounded-lg bg-zinc-800 border border-zinc-700">
                        <div className="flex items-center gap-1.5 text-zinc-500 mb-0.5"><Truck className="w-3 h-3" /><span>Trasportatore</span></div>
                        <span className="text-white font-medium text-xs">{fir.trasportatore_nome || "-"}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                      <div className="flex-1 p-2.5 rounded-lg bg-zinc-800 border border-zinc-700">
                        <div className="flex items-center gap-1.5 text-zinc-500 mb-0.5"><Building2 className="w-3 h-3" /><span>Destinatario</span></div>
                        <span className="text-white font-medium text-xs">{fir.destinatario_nome || "-"}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800">
                      <span className="text-zinc-500 text-xs">Peso: <span className="text-white font-medium">{fir.quantita} {fir.unita_misura}</span></span>
                      <span className="text-zinc-500 text-xs">{fir.descrizione_rifiuto}</span>
                    </div>
                    {fir.codice_blocco && (
                      <div className="mt-2 text-xs text-zinc-600">Blocco: <span className="text-zinc-400 font-mono">{fir.codice_blocco}</span>
                        {fir.vidimazione_numero && <> - N. <span className="text-emerald-400 font-mono">{fir.vidimazione_numero}</span></>}
                      </div>
                    )}
                    {fir.stato === "errore" && fir.last_error && (
                      <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                        <p className="text-red-400 text-xs truncate" title={fir.last_error}>{fir.last_error}</p>
                        {fir.last_error.toLowerCase().includes("blocco") && (
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Codice Blocco FIR"
                              className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-xs outline-none focus:border-emerald-500"
                              data-testid={`blocco-input-${fir.numero_fir}`}
                              onKeyDown={async (e) => {
                                if (e.key === "Enter" && e.target.value.trim()) {
                                  const res = await api(`/fir/${fir.id}/blocco`, {
                                    method: "PATCH",
                                    body: JSON.stringify({ codice_blocco: e.target.value.trim() })
                                  });
                                  if (res && res.success) {
                                    toast.success("Codice blocco salvato. Ora puoi emettere il FIR.");
                                    loadFir();
                                  } else {
                                    toast.error("Errore nel salvataggio");
                                  }
                                }
                              }}
                            />
                            <button
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded"
                              data-testid={`save-blocco-${fir.numero_fir}`}
                              onClick={async (e) => {
                                const input = e.target.parentElement.querySelector("input");
                                if (input?.value.trim()) {
                                  const res = await api(`/fir/${fir.id}/blocco`, {
                                    method: "PATCH",
                                    body: JSON.stringify({ codice_blocco: input.value.trim() })
                                  });
                                  if (res && res.success) {
                                    toast.success("Codice blocco salvato. Ora puoi emettere il FIR.");
                                    loadFir();
                                  } else {
                                    toast.error("Errore nel salvataggio");
                                  }
                                }
                              }}
                            >Salva</button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-12 text-center">
                <FileCheck className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">Nessun FIR presente. Clicca "Nuovo xFIR" per crearne uno.</p>
              </CardContent>
            </Card>
          )}

          {/* Info section */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-blue-500/15 flex-shrink-0"><Info className="w-5 h-5 text-blue-400" /></div>
                <div>
                  <h3 className="text-white font-medium text-sm">Come funziona il FIR Digitale (xFIR)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                    {[
                      { n: "1", text: 'Il produttore crea il FIR e lo firma digitalmente', actor: "produttore" },
                      { n: "2", text: 'Il trasportatore accetta e compila la sua sezione', actor: "trasportatore" },
                      { n: "3", text: 'Il destinatario conferma il ricevimento e chiude il FIR', actor: "destinatario" },
                    ].map(s => (
                      <div key={s.n} className="flex items-start gap-2">
                        <span className="text-emerald-400 font-bold text-sm">{s.n}.</span>
                        <p className="text-zinc-400 text-xs">Il <strong className="text-zinc-300">{s.actor}</strong> {s.text.split(s.actor)[1]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <FirFormModal open={showFirForm} onClose={() => setShowFirForm(false)} onSaved={loadFir} cerCodes={cerCodes} />
        </div>
      )}
    </div>
  );
}
